import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  HStack,
  VStack,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Spacer,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCaption,
  TableContainer,
  Button,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Icon,
} from '@chakra-ui/react';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  FiPlus,
  FiDownload,
  FiShare,
  FiMoreVertical,
  FiTrash2,
} from 'react-icons/fi';
import moment from 'moment';

import { Fetch } from 'utils/fetch';
import { useToast } from 'utils/toast';
import { Layout } from 'views/components/layout';
import { Index as Response } from 'interfaces/api/references/index';
import { Reference } from 'interfaces/models/reference';

export const Index = () => {
  const navigate = useNavigate();
  const { sendToast } = useToast();
  const [response, setResponse] = React.useState<Response | undefined>(undefined);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [cardWidth, setCardWidth] = React.useState<number | undefined>(undefined);
  const [references, setReferences] = React.useState<undefined | Reference[]>(undefined);
  const [deleteItems, setDeleteItems] = React.useState<Array<{[key: string]: string}>>([]);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const handleDownload = (id: string, name: string) => {
    Fetch.download(`files/references/${id}`, name);
  };

  const handleOnDragEnd = (result: any) => {
    if (!references) return;
    if (!result.destination) return;
    const updatedItems = [...references];

    const [reorderedItem] = updatedItems.splice(result.source.index, 1);
    updatedItems.splice(result.destination.index, 0, reorderedItem);

    setReferences(updatedItems);

    let request: Array<string> = [];
    updatedItems.forEach((reference, _) => {
      request.push(reference.id);
    });

    Fetch.post('references/priorities', request).then(res => {
      if (res?.error) {
        return false;
      }
    });
  };

  const handleDeleteItem = (e: any, id: string, name: string) => {
    if (e.target.checked) {
      setDeleteItems([...deleteItems, {id: id, name: name}]);
    } else {
      setDeleteItems(deleteItems.filter(item => item.id !== id));
    }
  };

  const handleDeleteItems = () => {
    if (deleteItems.length === 0) return;

    let request: Array<string> = [];
    deleteItems.forEach((item, _) => {
      request.push(item.id);
    });

    Fetch.post('references/delete', request).then(res => {
      if (res?.error) {
        sendToast({title: '削除失敗しました', description: '', status: 'error'});
        return false;
      }

      setReferences(references?.filter(reference => !request.includes(reference.id)));
      setDeleteItems([]);
      setIsOpen(false);
    });
  };

  React.useEffect(() => {
    (async() => {
      await Fetch.get('references').then(res => {
        setLoading(false);

        if (res?.error) {
          return false;
        }

        setResponse(res);
        setCardWidth(document.getElementById('contentHeader')?.getBoundingClientRect()?.width);

        if (res?.references.length > 0) {
          setReferences(res.references);
        }
      });
    })()
  }, []);

  return (
    <>
    <Layout
      currentNavi='references'
      loading={loading}
      name={response?.my?.name}
      avatar={response?.my?.avatar}
    >
      <VStack
        w='full'
        h='full'
        p={3}
        maxW={cardWidth ? cardWidth : 'full'}
      >
        <Card
          w='full'
          h='full'
        >
          <CardHeader>
            <HStack>
              <Heading size='md'>参考資料一覧</Heading>
              <Spacer />
              <IconButton
                isRound={true}
                variant='solid'
                colorScheme='base'
                aria-label='plus'
                fontSize={20}
                icon={<FiPlus />}
                as={Link}
                to='/references/create'
              />
              <IconButton
                isRound={true}
                variant='solid'
                colorScheme='red'
                aria-label='plus'
                fontSize={20}
                icon={<FiTrash2 />}
                isDisabled={deleteItems.length === 0}
                onClick={() => setIsOpen(true)}
              />
            </HStack>
          </CardHeader>
          <CardBody>
            {(references && references.length > 0) &&
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId='droppable'>
                  {(provided) => (
                    <TableContainer>
                      <Table variant='striped'>
                        <TableCaption>参考資料一覧</TableCaption>
                        <Thead>
                          <Tr>
                            <Th></Th>
                            <Th>#</Th>
                            <Th>参考資料ファイル</Th>
                            <Th>更新日時</Th>
                          </Tr>
                        </Thead>
                        <Tbody
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {references.map((reference: Reference, index: number) => (
                            <Draggable key={reference.id} draggableId={reference.id} index={index}>
                              {(provided) => (
                                <Tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                >
                                  <Td
                                    maxW={1}
                                    cursor={'pointer'}
                                    {...provided.dragHandleProps}

                                  >
                                    <FiMoreVertical
                                    />
                                  </Td>
                                  <Td>
                                    <HStack>
                                      <>
                                      <Checkbox
                                        mr={2}
                                        colorScheme='red'
                                        size='lg'
                                        onChange={(e) => handleDeleteItem(e, reference.id, reference.name)}
                                      />
                                      <Icon
                                        as={FiShare}
                                        color='green.700'
                                        w={5}
                                        h={5}
                                        onClick={() => navigate(`/references/${reference.id}`)}
                                      />
                                      {reference.id}
                                      </>
                                    </HStack>
                                  </Td>
                                  <Td>
                                  <HStack>
                                      <>
                                      <Icon
                                        as={FiDownload}
                                        color='green.700'
                                        w={5}
                                        h={5}
                                        onClick={() => handleDownload(reference.id, reference.name)}
                                        />
                                      {reference.name}
                                      </>
                                    </HStack>
                                  </Td>
                                  <Td>{reference.updateAt ? moment.unix(reference.updateAt).format('YYYY/MM/DD HH:mm') : '-'}</Td>
                                </Tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </Droppable>
              </DragDropContext>
            }
          </CardBody>
        </Card>
      </VStack>
    </Layout>
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(true)}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>データを削除しますか？</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table variant='striped'>
            <Tbody>
              {deleteItems.map((item, index) => (
                <Tr key={index}>
                  <Td>{index + 1}</Td>
                  <Td>{item.name}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={() => setIsOpen(false)}>
            キャンセル
          </Button>
          <Button
            colorScheme='red'
            onClick={handleDeleteItems}
          >削除する</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
};
